/**
 * Group-invite links: `<origin>/chats/invite/<conversationId>` (built in
 * `profile-info-panel.tsx`, rendered by the `/chats/invite/:token` route).
 *
 * One parser shared by the two places that need to recognise one: the router
 * effect in `PresencePage` (deciding whether the CURRENT location is one) and
 * the message bubble (deciding whether a link typed into a chat should open
 * through the router, in this tab, instead of behaving like any other
 * address).
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const INVITE_PATH_RE = /^\/chats\/invite\/([^/?#]+)\/?$/i

/** The conversation id in an invite path, or null for anything else. */
export function inviteIdFromPath(pathname: string): string | null {
  const m = INVITE_PATH_RE.exec(pathname)
  return m && UUID_RE.test(m[1]) ? m[1] : null
}

/**
 * The conversation id in an invite link, but only for one that points at
 * THIS origin. A link to another deployment (or a look-alike on another
 * site) cannot be resolved in-app, so it is left to behave like any other
 * external address: new tab, no confirmation dialog, no join.
 */
export function inviteIdFromHref(href: string, currentOrigin: string): string | null {
  let url: URL
  try {
    url = new URL(href, currentOrigin)
  } catch {
    return null
  }
  return url.origin === currentOrigin ? inviteIdFromPath(url.pathname) : null
}

/** What an invite link's `getConversation` lookup means for the person who clicked it. */
export type InviteMembership = "open" | "confirm" | "error"

/**
 * Turn a failed (or successful) membership lookup into what to do next.
 *
 * `getConversation` succeeding IS membership: the server only serves that
 * route to a current member, so a non-member never needs its own flag. It
 * shows up here as the specific failure the server sends for "you aren't in
 * this one" (NOT_MEMBER, or a plain 403 from a server that predates the
 * code). Anything else (network blip, deleted group, a real server error)
 * is not something a Join button fixes, so it falls through to an error
 * instead of quietly offering to add the person anyway.
 */
export function decideInviteMembership(
  outcome: { ok: true } | { ok: false; status?: number; code?: string },
): InviteMembership {
  if (outcome.ok) return "open"
  if (outcome.status === 403 || outcome.code === "NOT_MEMBER") return "confirm"
  return "error"
}
